import { useId } from "react";
import { cn } from "../../lib/utils";

// Festive decor for Diwali campaign pages. All decorative (aria-hidden),
// campaign tokens only, and every animation stops under prefers-reduced-motion.

/** Clay diya with a softly flickering flame. */
export function Diya({ className, animated = true }) {
  const id = useId();

  return (
    <svg
      viewBox="0 0 64 64"
      className={cn("overflow-visible", className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffc857" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ffc857" stopOpacity="0" />
        </radialGradient>

        <linearGradient id={`${id}-flame`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#ea580c" />
          <stop offset="45%" stopColor="#ffb547" />
          <stop offset="100%" stopColor="#fff3b0" />
        </linearGradient>

        <linearGradient id={`${id}-bowl`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d2551c" />
          <stop offset="100%" stopColor="#9a3412" />
        </linearGradient>
      </defs>

      <circle
        cx="32"
        cy="24"
        r="22"
        fill={`url(#${id}-glow)`}
        className={animated ? "animate-diya-glow" : undefined}
      />

      <g
        className={animated ? "animate-diya-flame" : undefined}
        style={{ transformOrigin: "32px 34px" }}
      >
        <path
          d="M32 10 C37 18 39 24 36 30 C34.5 33 29.5 33 28 30 C25 24 27 18 32 10 Z"
          fill={`url(#${id}-flame)`}
        />
        <path
          d="M32 20 C34 24 34.5 27 33 29.5 C32.3 30.6 31.7 30.6 31 29.5 C29.5 27 30 24 32 20 Z"
          fill="#fff8e1"
        />
      </g>

      <path
        d="M8 36 C10 48 20 54 32 54 C44 54 54 48 56 36 C46 40 18 40 8 36 Z"
        fill={`url(#${id}-bowl)`}
      />

      <path
        d="M8 36 C18 40 46 40 56 36 C52 33 44 32 32 32 C20 32 12 33 8 36 Z"
        fill="#f59e0b"
      />

      <path
        d="M14 44 Q32 50 50 44"
        stroke="#ffc857"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="1 4"
      />
    </svg>
  );
}

/** A string of festive lights hanging in shallow swags. Width-agnostic: it repeats and clips. */
export function LightString({ className, count = 18 }) {
  const colors = [
    "#ffb547",
    "#f97316",
    "#ffc857",
    "#ef4444",
    "#ffb547",
    "#22c55e",
  ];

  return (
    <div
      className={cn(
        "pointer-events-none flex w-full items-start overflow-visible",
        className
      )}
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => {
        const color = colors[i % colors.length];

        return (
          <svg
            key={i}
            viewBox="0 0 96 44"
            className={`min-w-0 flex-1 overflow-visible ${
              i >= 10
                ? "hidden lg:block"
                : i >= 7
                  ? "hidden sm:block"
                  : ""
            } h-6 sm:h-8 md:h-10 lg:h-11`}
            focusable="false"
          >
            <path
              d="M0 3 Q48 26 96 3"
              stroke="#7c5a3a"
              strokeOpacity="0.45"
              strokeWidth="1.2"
              fill="none"
            />

            <g
              className="animate-twinkle"
              style={{ animationDelay: `${(i % 6) * 0.45}s` }}
            >
              <circle cx="48" cy="24" r="7" fill={color} opacity="0.18" />

              <rect
                x="45.5"
                y="13"
                width="5"
                height="4"
                rx="1"
                fill="#7c5a3a"
                fillOpacity="0.6"
              />

              <path
                d="M48 17 C52 21 52.5 25 50.5 28 C49.5 29.5 46.5 29.5 45.5 28 C43.5 25 44 21 48 17 Z"
                fill={color}
              />
            </g>
          </svg>
        );
      })}
    </div>
  );
}